import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';

const GroupTherapy = () => {
  const [activeSection, setActiveSection] = useState('overview');
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);
  const [symptomsCanScrollLeft, setSymptomsCanScrollLeft] = useState(false);
  const [symptomsCanScrollRight, setSymptomsCanScrollRight] = useState(true);
  const [causesCanScrollLeft, setCausesCanScrollLeft] = useState(false);
  const [causesCanScrollRight, setCausesCanScrollRight] = useState(true);
  const [typesCanScrollLeft, setTypesCanScrollLeft] = useState(false);
  const [typesCanScrollRight, setTypesCanScrollRight] = useState(true);
  const symptomsScrollRef = useRef(null);
  const causesScrollRef = useRef(null);
  const typesScrollRef = useRef(null);

  // Scroll detection for active section
  useEffect(() => {
    const handleScroll = () => {
      const sections = ['overview', 'symptoms', 'causes', 'types'];
      const scrollPosition = window.scrollY + 250; // Offset for better detection

      for (const section of sections) {
        const element = document.getElementById(section);
        if (element) {
          const { offsetTop, offsetHeight } = element;
          if (scrollPosition >= offsetTop && scrollPosition < offsetTop + offsetHeight) {
            setActiveSection(section);
            break;
          }
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll(); // Initial check
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const filterContent = (section) => {
    setActiveSection(section);
  };

  const checkScrollPosition = (ref) => {
    if (ref.current) {
      const { scrollLeft, scrollWidth, clientWidth } = ref.current;
      const canScrollLeft = scrollLeft > 0;
      const canScrollRight = scrollLeft < scrollWidth - clientWidth - 1;
      return { canScrollLeft, canScrollRight };
    }
    return { canScrollLeft: false, canScrollRight: false };
  };

  const scrollSymptoms = (direction) => {
    if (symptomsScrollRef.current) {
      const scrollAmount = 320; // Card width + gap
      symptomsScrollRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });
      setTimeout(() => {
        const position = checkScrollPosition(symptomsScrollRef);
        setSymptomsCanScrollLeft(position.canScrollLeft);
        setSymptomsCanScrollRight(position.canScrollRight);
      }, 300);
    }
  };

  const scrollCauses = (direction) => {
    if (causesScrollRef.current) {
      const scrollAmount = 320;
      causesScrollRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });
      setTimeout(() => {
        const position = checkScrollPosition(causesScrollRef);
        setCausesCanScrollLeft(position.canScrollLeft);
        setCausesCanScrollRight(position.canScrollRight);
      }, 300);
    }
  };

  const scrollTypes = (direction) => {
    if (typesScrollRef.current) {
      const scrollAmount = 320;
      typesScrollRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });
      setTimeout(() => {
        const position = checkScrollPosition(typesScrollRef);
        setTypesCanScrollLeft(position.canScrollLeft);
        setTypesCanScrollRight(position.canScrollRight);
      }, 300);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-[#E6F7F4]"></div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-14 lg:py-16">
          <div className="relative overflow-hidden rounded-3xl bg-[#E6F7F4]">
            <div className="relative px-6 py-24 sm:px-10 sm:py-32 lg:px-12 lg:py-36 text-center">
              <img
                src="/static/img/services/group.png"
                alt=""
                className="pointer-events-none absolute -right-2 top-1/2 hidden w-80 h-96 -translate-y-1/2 opacity-75 lg:block"
              />

              <div className="mx-auto flex items-center justify-center gap-2 text-xs tracking-wide text-gray-700">
                <span>Child Services</span>
              </div>

              <h1 className="mt-6 font-extrabold tracking-tight text-gray-900 text-4xl sm:text-6xl lg:text-7xl leading-[1.05]">
                Group Therapy
              </h1>

              <p className="mt-6 text-sm sm:text-base text-gray-600 max-w-2xl mx-auto">
                Collaborative healing in a supportive group environment. Peer interaction, social skills development, and shared experiences to foster emotional growth and connection.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Action Bar */}
      <div className="bg-white border-b border-gray-200 sticky top-20 z-30 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap items-center justify-center py-4">
            <div className="flex flex-wrap items-center gap-6">
              <a 
                href="#overview" 
                className={`font-medium transition-colors ${
                  activeSection === 'overview' 
                    ? 'text-[#00A785]' 
                    : 'text-gray-700 hover:text-[#00A785]'
                }`}
                onClick={(e) => {
                  e.preventDefault();
                  const element = document.getElementById('overview');
                  const offset = 200; // Height of main navbar + action bar + extra spacing + padding
                  const elementPosition = element.getBoundingClientRect().top;
                  const offsetPosition = elementPosition + window.pageYOffset - offset;
                  window.scrollTo({
                    top: offsetPosition,
                    behavior: 'smooth'
                  });
                }}
              >
                Overview
              </a>
              <a 
                href="#symptoms" 
                className={`font-medium transition-colors ${
                  activeSection === 'symptoms' 
                    ? 'text-[#00A785]' 
                    : 'text-gray-700 hover:text-[#00A785]'
                }`}
                onClick={(e) => {
                  e.preventDefault();
                  const element = document.getElementById('symptoms');
                  const offset = 200;
                  const elementPosition = element.getBoundingClientRect().top;
                  const offsetPosition = elementPosition + window.pageYOffset - offset;
                  window.scrollTo({
                    top: offsetPosition,
                    behavior: 'smooth'
                  });
                }}
              >
                Symptoms
              </a>
              <a 
                href="#causes" 
                className={`font-medium transition-colors ${
                  activeSection === 'causes' 
                    ? 'text-[#00A785]' 
                    : 'text-gray-700 hover:text-[#00A785]'
                }`}
                onClick={(e) => {
                  e.preventDefault();
                  const element = document.getElementById('causes');
                  const offset = 200;
                  const elementPosition = element.getBoundingClientRect().top;
                  const offsetPosition = elementPosition + window.pageYOffset - offset;
                  window.scrollTo({
                    top: offsetPosition,
                    behavior: 'smooth'
                  });
                }}
              >
                Benefits
              </a>
              <a 
                href="#types" 
                className={`font-medium transition-colors ${
                  activeSection === 'types' 
                    ? 'text-[#00A785]' 
                    : 'text-gray-700 hover:text-[#00A785]'
                }`}
                onClick={(e) => {
                  e.preventDefault();
                  const element = document.getElementById('types');
                  const offset = 200;
                  const elementPosition = element.getBoundingClientRect().top;
                  const offsetPosition = elementPosition + window.pageYOffset - offset;
                  window.scrollTo({
                    top: offsetPosition,
                    behavior: 'smooth'
                  });
                }}
              >
                Types
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          
          {/* Overview */}
          <div id="overview" className="mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-[#00A785] mb-4">Overview</h2>
            <p className="text-lg text-gray-600 mb-8">
              Group therapy is a form of psychotherapy in which a small group of individuals meets regularly, typically led by a trained therapist or counselor. 
              It provides a supportive and structured environment for participants to discuss and explore their thoughts, feelings, and experiences, with the goal of personal growth and improved mental well-being.
            </p>
            <p className="text-lg text-gray-600 mb-8">
              Group therapy provides children a safe environment to practice skills and techniques learned in individual sessions. 
              Group therapy gives therapists an opportunity to see how kids relate to one another in a group setting. 
              For kids struggling with social anxiety, poor social skills, or inattentiveness, the group setting offers the therapist a chance to help kids work through their obstacles with other kids as they occur. 
              Group therapy can also be a source of support for kids dealing with grief, bullying, anxiety and depression, and other mental health issues.
            </p>
          </div>
        </div>

        {/* Symptoms Section with Full-Width Background */}
        <div id="symptoms" className="py-16 bg-[#00A785]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">Symptoms </h2>
            
            {/* Common Signs */}
            <div>
              <h3 className="text-lg font-normal text-white mb-6">Recognizing signs that may indicate the need for group therapy services</h3>
              
              {/* Scroll Container */}
              <div 
                ref={symptomsScrollRef}
                className="overflow-x-auto scrollbar-hide mb-4"
              >
                <div className="flex space-x-4 py-4 w-fit">
                  {[
                    {
                      symptom: "Social Isolation",
                      icon: (
                        <svg className="w-6 h-6 text-green-500 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5.121 17.804A13.937 13.937 0 0112 16c2.5 0 4.847.655 6.879 1.804M15 10a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                      )
                    },
                    {
                      symptom: "Difficulty Making Friends",
                      icon: (
                        <svg className="w-6 h-6 text-green-500 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                        </svg>
                      )
                    },
                    {
                      symptom: "Poor Communication Skills",
                      icon: (
                        <svg className="w-6 h-6 text-green-500 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                        </svg>
                      )
                    },
                    {
                      symptom: "Low Self-Esteem",
                      icon: (
                        <svg className="w-6 h-6 text-green-500 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 17h8m0 0v-8m0 8l-8-8-4 4-6-6" />
                        </svg>
                      )
                    },
                    {
                      symptom: "Emotional Outbursts",
                      icon: (
                        <svg className="w-6 h-6 text-green-500 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      )
                    },
                    {
                      symptom: "Anxiety in Social Situations",
                      icon: (
                        <svg className="w-6 h-6 text-green-500 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      )
                    }
                  ].map((item, index) => (
                    <div key={index} className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm hover:shadow-md transition-shadow flex-shrink-0 w-[300px]">
                      <div className="flex items-start space-x-3">
                        <div className="flex-shrink-0">
                          {item.icon}
                        </div>
                        <div>
                          <h4 className="text-lg font-medium text-gray-900 mb-2">{item.symptom}</h4>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Navigation Buttons */}
              <div className="flex justify-center items-center space-x-4">
                <button
                  onClick={() => scrollSymptoms('left')}
                  disabled={!symptomsCanScrollLeft}
                  className={`border border-gray-200 rounded-lg p-3 shadow-lg hover:shadow-xl transition-shadow ${
                    symptomsCanScrollLeft 
                      ? 'bg-white text-gray-600 hover:bg-gray-50' 
                      : 'bg-gray-100 text-gray-300 cursor-not-allowed'
                  }`}
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                </button>

                <button
                  onClick={() => scrollSymptoms('right')}
                  disabled={!symptomsCanScrollRight}
                  className={`border border-gray-200 rounded-lg p-3 shadow-lg hover:shadow-xl transition-shadow ${
                    symptomsCanScrollRight 
                      ? 'bg-white text-gray-600 hover:bg-gray-50' 
                      : 'bg-gray-100 text-gray-300 cursor-not-allowed'
                  }`}
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Hide Scrollbar Styles */}
        <style jsx>{`
          .scrollbar-hide::-webkit-scrollbar {
            display: none;
          }
          .scrollbar-hide {
            -ms-overflow-style: none;
            scrollbar-width: none;
          }
        `}</style>

        {/* Benefits Section with Full-Width Background */}
        <div id="causes" className="bg-gray-100 py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl md:text-4xl font-bold text-[#00A785] mb-4">Benefits of Group Therapy</h2>
            
            {/* Key Benefits */}
            <div>
              <h3 className="text-lg font-normal text-gray-700 mb-6">How group therapy supports children's social and emotional development</h3>
              
              {/* Grid Container */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {[
                  {
                    title: "Social Skills Development",
                    description: "Assists children in developing their communication skills, cooperation, and relationship-building skills within a peer group.",
                    icon: (
                      <svg className="w-6 h-6 text-green-500 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                      </svg>
                    )
                  },
                  {
                    title: "Peer Support & Understanding",
                    description: "Children understand that they are not alone in their problems and receive support from their peers, who understand their problems.",
                    icon: (
                      <svg className="w-6 h-6 text-green-500 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                      </svg>
                    )
                  },
                  {
                    title: "Confidence Building",
                    description: "Children build their self-confidence through their social skills development and positive peer feedback.",
                    icon: (
                      <svg className="w-6 h-6 text-green-500 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                      </svg>
                    )
                  },
                  {
                    title: "Emotional Regulation",
                    description: "Assists children in understanding their emotions and how to manage them through group discussions.",
                    icon: (
                      <svg className="w-6 h-6 text-green-500 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M21 10c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 13.042 3 11.574 3 10c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                      </svg>
                    )
                  },
                  {
                    title: "Problem-Solving Skills",
                    description: "Assists children in learning how to work together to solve problems, promoting critical thinking skills.",
                    icon: (
                      <svg className="w-6 h-6 text-green-500 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M9 21h6a2 2 0 002-2v-1a3 3 0 00-3-3H10a3 3 0 00-3 3v1a2 2 0 002 2zm3-18a4 4 0 00-4 4v2a2 2 0 002 2h4a2 2 0 002-2V7a4 4 0 00-4-4z" />
                      </svg>
                    )
                  },
                  {
                    title: "Safe Practice Environment",
                    description: "Assists children in developing a structured environment where they feel comfortable practicing their skills without fear of being judged.",
                    icon: (
                      <svg className="w-6 h-6 text-green-500 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622C17.176 19.29 21 14.59 21 9c0-1.042-.133-2.052-.382-3.016z" />
                      </svg>
                    )
                  }
                ].map((benefit, index) => (
                  <div key={index} className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm hover:shadow-md transition-shadow">
                    <div className="flex items-start space-x-3">
                      <div className="flex-shrink-0">
                        {benefit.icon}
                      </div>
                      <div>
                        <h4 className="text-lg font-medium text-gray-900 mb-2">{benefit.title}</h4>
                        <p className="text-sm text-gray-600">{benefit.description}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Types Section with Full-Width Background */}
        <div id="types" className="py-16 bg-[#00A785]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">Types of Group Therapy</h2>
            
            <div>
              <h3 className="text-lg font-normal text-white mb-6">Different therapeutic approaches for various needs and age groups</h3>
              
              {/* Grid Container */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {[
                  {
                    title: "Cognitive Behavioral Therapy Groups",
                    description: "Assists children in changing negative thinking patterns and dealing with feelings of anxiety, anger, and sadness through group activities.",
                    icon: (
                      <svg className="w-6 h-6 text-green-500 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                      </svg>
                    )
                  },
                  {
                    title: "Social Skills Groups",
                    description: "Assists children in learning how to make friends and how to talk to people and understand how people feel through fun role-playing exercises.",
                    icon: (
                      <svg className="w-6 h-6 text-green-500 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                      </svg>
                    )
                  },
                  {
                    title: "Support Groups",
                    description: "Creates a safe environment where children can talk about their feelings about a certain experience and see that they are not alone.",
                    icon: (
                      <svg className="w-6 h-6 text-green-500 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                      </svg>
                    )
                  },
                  {
                    title: "Play Therapy Groups",
                    description: "Assists younger children in expressing how they feel and solving problems through play and games in a natural and fun way.",
                    icon: (
                      <svg className="w-6 h-6 text-green-500 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.828 14.828a4 4 0 01-5.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    )
                  },
                  {
                    title: "DBT Skills Groups",
                    description: "Assists teens in learning how to deal with their emotions, stress, and staying focused through learning life skills.",
                    icon: (
                      <svg className="w-6 h-6 text-green-500 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                      </svg>
                    )
                  },
                  {
                    title: "Art Therapy Groups",
                    description: "Assists children in expressing how they feel through drawing and painting when it is difficult to talk about feelings.",
                    icon: (
                      <svg className="w-6 h-6 text-green-500 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                    )
                  },
                  {
                    title: "Learning Groups",
                    description: "Assists children in learning about their problems, such as ADHD and autism, and how to deal with them better.",
                    icon: (
                      <svg className="w-6 h-6 text-green-500 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                      </svg>
                    )
                  }
                ].map((type, index) => (
                  <div key={index} className="bg-gray-50 border border-gray-200 rounded-lg p-6 shadow-sm hover:shadow-md transition-shadow">
                    <div className="flex items-start space-x-3">
                      <div className="flex-shrink-0">
                        {type.icon}
                      </div>
                      <div>
                        <h4 className="text-lg font-medium text-gray-900 mb-2">{type.title}</h4>
                        <p className="text-sm text-gray-600">{type.description}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default GroupTherapy;
